import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import PageTour from '@/components/PageTour';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Mail, MessageCircle, Bell, Send, Upload, Download, Database, Shield, ShieldCheck, Clock, Play, FileCheck, Camera, Wifi, WifiOff, Video, Users, AlertTriangle, ExternalLink, Link, Lock, Trash2, Eye, EyeOff, Settings, Fingerprint, ScanFace, Watch, Hand, MapPin, Plus, X, Smartphone, ArrowLeft } from 'lucide-react';
import api from '@/lib/api';
import { invalidateConfigCache } from '@/lib/configCache';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ResetModuleTourButton } from '@/components/ModuleTour';
import { useBranchStore } from '@/stores';
import SampleDataCard from '@/components/settings/SampleDataCard';
import ComplianceAlertsCard from '@/components/settings/ComplianceAlertsCard';
import PhantomSupplierPaymentsCard from '@/components/settings/PhantomSupplierPaymentsCard';
import DataHygieneTab from '@/components/settings/DataHygieneTab';
import FeatureFlagsCard from '@/components/settings/FeatureFlagsCard';
import StrictFundGuardCard from '@/components/settings/StrictFundGuardCard';
import JobHealthCard from '@/components/settings/JobHealthCard';
import ApkAdminCard from '@/components/settings/ApkAdminCard';
import ApkCiStatusCard from '@/components/settings/ApkCiStatusCard';
import { SettingsHub } from '@/components/settings/SettingsHub';
import { SettingsCCTVTab } from '@/components/settings/tabs/SettingsCCTVTab';
import { SettingsEmailTab } from '@/components/settings/tabs/SettingsEmailTab';
import { SettingsNotificationsTab } from '@/components/settings/tabs/SettingsNotificationsTab';
import { SettingsBackupTab } from '@/components/settings/tabs/SettingsBackupTab';
import { SettingsSchedulerTab } from '@/components/settings/tabs/SettingsSchedulerTab';
import { SettingsZatcaTab } from '@/components/settings/tabs/SettingsZatcaTab';
import { SettingsAttendanceTab } from '@/components/settings/tabs/SettingsAttendanceTab';
import { SSoTCompanyInfoBanner } from '@/components/common/SSoTCompanyInfoBanner';
import { localToday } from '@/lib/dateRange';

function BranchWaButtons() {
  const { branches: br, fetchBranches } = useBranchStore();
  useEffect(() => { fetchBranches(); }, []);
  return (
    <div className="flex gap-2 flex-wrap">
      <Button variant="outline" className="rounded-xl h-8 text-xs" onClick={async () => { try { const res = await api.post('/whatsapp/send-branch-report', {}); toast.success(res.data.message); } catch(e) { toast.error(e.response?.data?.detail || 'Failed'); } }}>All Branches</Button>
      {br.map(b => (
        <Button key={b.id} variant="outline" className="rounded-xl h-8 text-xs" onClick={async () => { try { const res = await api.post('/whatsapp/send-branch-report', { branch_id: b.id }); toast.success(res.data.message); } catch(e) { toast.error(e.response?.data?.detail || 'Failed'); } }}>{b.name}</Button>
      ))}
    </div>
  );
}

export default function SettingsPage() {
  const [emailSettings, setEmailSettings] = useState({ smtp_host: '', smtp_port: 587, username: '', password: '', from_email: '', use_tls: true });
  const [activeTab, setActiveTab] = useState('hub');
  const [whatsappSettings, setWhatsappSettings] = useState({ account_sid: '', auth_token: '', phone_number: '', recipient_number: '', enabled: true });
  const [notifPrefs, setNotifPrefs] = useState({ email_daily_sales: false, email_document_expiry: true, email_leave_updates: false, whatsapp_daily_sales: false, whatsapp_document_expiry: false });
  const [companyInfo, setCompanyInfo] = useState({ company_name: 'Smarto', address_line1: '', address_line2: '', city: '', country: '', phone: '', email: '', cr_number: '', vat_number: '', vat_enabled: false, vat_rate: 15 });
  const [zatcaSettings, setZatcaSettings] = useState({
    enabled: false, environment: 'sandbox', otp: '',
    csid: '', csid_secret: '', production_csid: '', production_secret: '',
    certificate: '', private_key: '',
    auto_submit: false, invoice_counter: 1,
    csid_expiry: '', production_csid_expiry: '', expiry_alert_days: 30
  });
  const [zatcaStatus, setZatcaStatus] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [schedulerJobs, setSchedulerJobs] = useState([]);
  const [schedulerLogs, setSchedulerLogs] = useState([]);
  const [cctvSettings, setCctvSettings] = useState({
    hik_email: '', hik_password: '', hik_status: null,
    people_counting_enabled: true, motion_alerts_enabled: true,
    alert_sensitivity: 'medium', counting_interval: 5
  });
  const [monitoringConfig, setMonitoringConfig] = useState({
    enabled: false, interval_minutes: 5,
    cameras: [], features: ['people_counting', 'motion_detection'],
    notification_channels: ['in_app']
  });
  const { branches, fetchBranches: fetchBranchesStore } = useBranchStore();
  useEffect(() => { fetchBranchesStore(); }, []);
  const [dvrs, setDvrs] = useState([]);
  const [newDVR, setNewDVR] = useState({
    branch_id: '', name: '', ip_address: '', port: 8000,
    username: 'admin', password: '', device_serial: '', is_cloud: true, channels: 4
  });
  const [attSettings, setAttSettings] = useState({ allowed_methods: ['manual', 'fingerprint', 'face', 'watch'], require_location: false, auto_timeout_hours: 0, geofence: { enabled: false, locations: [] } });
  const [alertSettings, setAlertSettings] = useState({ enabled: false, late_threshold_minutes: 15, absence_check_time: '10:00', consecutive_absence_days: 3, notify_roles: ['admin'], branch_overrides: [] });

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const [emailRes, waRes, prefRes, coRes, schedRes, logRes, hikRes, _branchRes, dvrRes, monitorRes, zatcaRes, zatcaStatusRes] = await Promise.all([
        api.get('/settings/email').catch(() => ({ data: null })),
        api.get('/settings/whatsapp').catch(() => ({ data: null })),
        api.get('/settings/notifications').catch(() => ({ data: null })),
        api.get('/settings/company').catch(() => ({ data: null })),
        api.get('/scheduler/config').catch(() => ({ data: [] })),
        api.get('/scheduler/logs').catch(() => ({ data: [] })),
        api.get('/cctv/hik-connect/status').catch(() => ({ data: null })),
        Promise.resolve({ data: [] }), // branches handled by Zustand store
        api.get('/cctv/dvrs').catch(() => ({ data: [] })),
        api.get('/cctv/monitoring/config').catch(() => ({ data: null })),
        api.get('/settings/zatca').catch(() => ({ data: null })),
        api.get('/settings/zatca/status').catch(() => ({ data: null })),
      ]);
      if (emailRes.data) setEmailSettings(prev => ({ ...prev, ...emailRes.data }));
      if (waRes.data) setWhatsappSettings(prev => ({ ...prev, ...waRes.data }));
      if (prefRes.data) setNotifPrefs(prev => ({ ...prev, ...prefRes.data }));
      if (coRes.data) setCompanyInfo(prev => ({ ...prev, ...coRes.data }));
      if (schedRes.data) setSchedulerJobs(schedRes.data);
      if (logRes.data) setSchedulerLogs(logRes.data);
      if (hikRes.data) setCctvSettings(prev => ({ ...prev, hik_email: hikRes.data.email || '', hik_status: hikRes.data }));
      // branches loaded from useBranchStore
      if (dvrRes.data) setDvrs(dvrRes.data);
      if (monitorRes.data) setMonitoringConfig(prev => ({ ...prev, ...monitorRes.data }));
      if (zatcaRes.data) setZatcaSettings(prev => ({ ...prev, ...zatcaRes.data }));
      if (zatcaStatusRes.data) setZatcaStatus(zatcaStatusRes.data);
      // Fetch attendance settings
      try { const attRes = await api.get('/attendance/settings'); if (attRes.data) setAttSettings(prev => ({ ...prev, ...attRes.data })); } catch {}
      try { const alertRes = await api.get('/attendance/alert-settings'); if (alertRes.data) setAlertSettings(prev => ({ ...prev, ...alertRes.data })); } catch {}
    } catch {}
    finally { setLoading(false); }
  };

  const saveEmail = async () => {
    try { await api.post('/settings/email', emailSettings); toast.success('Email settings saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const testEmailSend = async () => {
    try {
      const res = await api.post('/settings/email/test', { to_email: testEmail || undefined });
      toast.success(res.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
  };

  const saveWhatsapp = async () => {
    try { await api.post('/settings/whatsapp', whatsappSettings); toast.success('WhatsApp settings saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const testWhatsapp = async () => {
    try {
      const res = await api.post('/settings/whatsapp/test');
      toast.success(res.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
  };

  const savePrefs = async () => {
    try { await api.post('/settings/notifications', notifPrefs); toast.success('Preferences saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const sendDailyReport = async () => {
    try {
      const res = await api.post('/send-daily-report');
      toast.success(res.data.message);
      if (res.data.details) res.data.details.forEach(d => toast.info(d));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleLogoUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post('/settings/upload-logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo uploaded');
    } catch { toast.error('Upload failed'); }
  };

  const saveHikConnect = async () => {
    try {
      await api.post('/cctv/hik-connect/auth', { email: cctvSettings.hik_email, password: cctvSettings.hik_password });
      toast.success('Hik-Connect credentials saved');
      fetchSettings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const saveCctvSettings = async () => {
    try {
      await api.post('/cctv/settings', {
        people_counting_enabled: cctvSettings.people_counting_enabled,
        motion_alerts_enabled: cctvSettings.motion_alerts_enabled,
        alert_sensitivity: cctvSettings.alert_sensitivity,
        counting_interval: cctvSettings.counting_interval
      });
      toast.success('CCTV settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const addDVR = async () => {
    try {
      const branch = branches.find(b => b.id === newDVR.branch_id);
      await api.post('/cctv/dvrs', { ...newDVR, branch_name: branch?.name || '' });
      toast.success('DVR added successfully');
      setNewDVR({ branch_id: '', name: '', ip_address: '', port: 8000, username: 'admin', password: '', device_serial: '', is_cloud: true, channels: 4 });
      fetchSettings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add DVR'); }
  };

  const deleteDVR = async (dvrId) => {
    if (!confirm('Delete this DVR and all its cameras?')) return;
    try {
      await api.delete(`/cctv/dvrs/${dvrId}`);
      toast.success('DVR deleted');
      fetchSettings();
    } catch { toast.error('Failed to delete DVR'); }
  };

  const saveMonitoringConfig = async () => {
    try {
      await api.post('/cctv/monitoring/config', monitoringConfig);
      toast.success('Monitoring configuration saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const runMonitoringNow = async () => {
    try {
      const res = await api.post('/cctv/monitoring/run');
      toast.success(res.data.message || 'Monitoring task queued');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to run'); }
  };

  const saveZatcaSettings = async () => {
    try {
      await api.post('/settings/zatca', zatcaSettings);
      toast.success('ZATCA settings saved successfully');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save ZATCA settings'); }
  };

  const testZatcaConnection = async () => {
    try {
      const res = await api.post('/settings/zatca/test');
      if (res.data.success) {
        toast.success(res.data.message || 'ZATCA connection successful');
      } else {
        toast.error(res.data.message || 'ZATCA connection failed');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to test connection'); }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64">Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-4">

            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-stone-500 to-zinc-600 flex items-center justify-center shadow-lg shadow-stone-200/50">

              <Settings size={24} className="text-white" />

            </div>

            <div>

              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="settings-title">Settings</h1>

              <p className="text-sm text-muted-foreground mt-0.5">System configuration and preferences</p>

            </div>

          </div>
          <div className="flex items-center gap-2">
            <PageTour storageKey="tour_settings" steps={[
              { target: '[data-testid="zatca-tab"]', title: 'ZATCA Compliance', content: 'Configure Saudi e-invoicing (ZATCA Phase 2) for tax compliance.' },
              { target: '[data-testid="access-policies-tab"]', title: 'Access Control', content: 'Set IP restrictions, session timeouts, and brute-force protection policies.' },
              { target: '[data-testid="attendance-config-tab"]', title: 'Attendance Config', content: 'Configure work hours, overtime rules, and late arrival thresholds.' },
            ]} />
          </div>
          <p className="text-muted-foreground">Configure email, WhatsApp, notifications & company settings</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {activeTab !== 'hub' && (
            <button onClick={() => setActiveTab('hub')} className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors" data-testid="settings-back-to-hub">
              <ArrowLeft size={16} /> All Settings
            </button>
          )}

          <TabsContent value="hub">
            <SettingsHub onOpenTab={setActiveTab} />
          </TabsContent>

          {/* Quick Links Tab */}
          <TabsContent value="email">
            <SettingsEmailTab
              emailSettings={emailSettings}
              setEmailSettings={setEmailSettings}
              saveEmail={saveEmail}
              testEmail={testEmail}
              setTestEmail={setTestEmail}
              testEmailSend={testEmailSend}
            />
          </TabsContent>

          <TabsContent value="whatsapp">
            <Card className="border-border">
              <CardHeader><CardTitle className="font-outfit">WhatsApp (Twilio) Configuration</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">Get your Twilio credentials from <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="text-primary underline">console.twilio.com</a>. Enable WhatsApp sandbox in Twilio first.</p>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Account SID *</Label><Input value={whatsappSettings.account_sid} placeholder="ACxxxxxxxxxx" data-testid="twilio-sid" onChange={(e) => setWhatsappSettings({ ...whatsappSettings, account_sid: e.target.value })} /></div>
                  <div><Label>Auth Token *</Label><Input type="password" value={whatsappSettings.auth_token} placeholder="••••••••" data-testid="twilio-token" onChange={(e) => setWhatsappSettings({ ...whatsappSettings, auth_token: e.target.value })} /></div>
                  <div><Label>WhatsApp Number (from Twilio) *</Label><Input value={whatsappSettings.phone_number} placeholder="+14155238886" onChange={(e) => setWhatsappSettings({ ...whatsappSettings, phone_number: e.target.value })} /></div>
                  <div><Label>Recipient Numbers * (comma-separated for multiple)</Label><Input value={whatsappSettings.recipient_number} placeholder="+966508235003, +966512345678" onChange={(e) => setWhatsappSettings({ ...whatsappSettings, recipient_number: e.target.value })} /><p className="text-xs text-muted-foreground mt-1">Add multiple numbers separated by comma. Each person must join Twilio sandbox first.</p></div>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button onClick={saveWhatsapp} className="rounded-full" data-testid="save-whatsapp">Save WhatsApp Settings</Button>
                  <Button variant="outline" onClick={testWhatsapp} className="rounded-full" data-testid="test-whatsapp"><Send size={14} className="mr-2" />Send Test</Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications">
            <SettingsNotificationsTab
              notifPrefs={notifPrefs}
              setNotifPrefs={setNotifPrefs}
              savePrefs={savePrefs}
              sendDailyReport={sendDailyReport}
              BranchWaButtons={BranchWaButtons}
              ComplianceAlertsCard={ComplianceAlertsCard}
            />
          </TabsContent>

          {/* ZATCA Phase 2 Settings Tab */}
          <TabsContent value="zatca">
            <SettingsZatcaTab
              zatcaSettings={zatcaSettings}
              setZatcaSettings={setZatcaSettings}
              zatcaStatus={zatcaStatus}
              saveZatcaSettings={saveZatcaSettings}
              testZatcaConnection={testZatcaConnection}
            />
          </TabsContent>

          <TabsContent value="attendance-config">
            <SettingsAttendanceTab
              attSettings={attSettings}
              setAttSettings={setAttSettings}
              alertSettings={alertSettings}
              setAlertSettings={setAlertSettings}
              branches={branches}
            />
          </TabsContent>

          {/* iter395g — System Maintenance tab. Houses admin-only data
              cleanup tools. Currently: phantom supplier-payment cleanup. */}
          <TabsContent value="maintenance">
            <div className="space-y-6" data-testid="maintenance-tab-content">
              <FeatureFlagsCard />
              <JobHealthCard />
              <StrictFundGuardCard />
              <PhantomSupplierPaymentsCard />
            </div>
          </TabsContent>

          <TabsContent value="hygiene">
            <DataHygieneTab />
          </TabsContent>

          {/* iter741 — Admin tool: upload new Android .apk + update apk-info.json. */}
          <TabsContent value="app-updates">
            <div className="space-y-6" data-testid="app-updates-tab-content">
              <ApkAdminCard />
              <ApkCiStatusCard />
            </div>
          </TabsContent>

          <TabsContent value="company">
            <div className="space-y-6">
              {/* ZATCA Invoicing Toggle - Prominent */}
              <Card className="border-orange-200 bg-orange-50/30" data-testid="zatca-settings-card">
                <CardHeader className="pb-3">
                  <CardTitle className="font-outfit flex items-center gap-2">
                    <FileCheck size={18} className="text-orange-600" />
                    ZATCA Invoicing
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">Enable or disable ZATCA-compliant VAT invoicing. When enabled, invoices will include VAT calculations and a QR code as required by Saudi tax authority.</p>
                  <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-orange-100">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-foreground">Enable ZATCA VAT on Invoices</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Adds VAT line items and QR code to all generated invoices</p>
                    </div>
                    <Switch
                      checked={companyInfo.vat_enabled}
                      onCheckedChange={(v) => setCompanyInfo({ ...companyInfo, vat_enabled: v })}
                      data-testid="zatca-toggle"
                    />
                  </div>
                  {companyInfo.vat_enabled && (
                    <div className="grid grid-cols-2 gap-4 p-4 bg-white rounded-xl border border-orange-100">
                      <div>
                        <Label className="text-xs">VAT Rate (%)</Label>
                        <Input type="number" value={companyInfo.vat_rate} onChange={(e) => setCompanyInfo({ ...companyInfo, vat_rate: e.target.value })} className="h-9 mt-1" data-testid="vat-rate-input" />
                      </div>
                      <div>
                        <Label className="text-xs">VAT Registration Number</Label>
                        <Input value={companyInfo.vat_number} onChange={(e) => setCompanyInfo({ ...companyInfo, vat_number: e.target.value })} className="h-9 mt-1" placeholder="3xxxxxxxxx00003" data-testid="vat-number-input" />
                      </div>
                    </div>
                  )}
                  <Button onClick={async () => {
                    try { await api.post('/settings/company', companyInfo); invalidateConfigCache('company_settings'); toast.success('ZATCA settings saved'); }
                    catch { toast.error('Failed'); }
                  }} className="rounded-xl bg-orange-600 hover:bg-orange-700" data-testid="save-zatca-btn-2">Save ZATCA Settings</Button>
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardHeader><CardTitle className="font-outfit">Company Information & Address</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-muted-foreground">Company name, address, contact details, CR, VAT and logo are now managed in one place — your <strong>Company Profile</strong> — and used automatically on letters, payslips, invoices, receipts and reports.</p>
                  <SSoTCompanyInfoBanner
                    editHref="/settings/company-profile"
                    scopeLabel="Company info"
                    previewFields={['company_name', 'company_vat', 'cr_number']}
                    data-testid="settings-company-ssot"
                  />
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardHeader><CardTitle className="font-outfit">Industry & Chart of Accounts</CardTitle></CardHeader>
                <CardContent>
                  <IndustryCoASeeder />
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardHeader><CardTitle className="font-outfit">Data Cleanup</CardTitle></CardHeader>
                <CardContent className="space-y-6">
                  <FixHistoricalCreditSales />
                  <div className="border-t border-border pt-6">
                    <PurgeTestData />
                    <ResetTransactionalData />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="import">
            <div className="space-y-6">
              <SampleDataCard />
              <Card className="border-border">
                <CardHeader><CardTitle className="font-outfit">Import Data from Excel/CSV</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">Upload your existing data from Excel (.xlsx) or CSV files. Download the template first, fill in your data, then upload.</p>
                  
                  {['customers', 'suppliers', 'employees', 'items', 'branches', 'sales', 'expenses_import'].map(type => (
                    <div key={type} className="flex items-center justify-between p-4 border rounded-xl hover:bg-muted/50 transition-all">
                      <div>
                        <div className="font-medium capitalize text-sm">{type}</div>
                        <div className="text-xs text-muted-foreground">
                          {type === 'customers' && 'name, phone, email'}
                          {type === 'suppliers' && 'name, category, phone, email, credit_limit'}
                          {type === 'employees' && 'name, document_id, phone, email, position, salary'}
                          {type === 'items' && 'name, unit_price, category'}
                          {type === 'branches' && 'name, location'}
                          {type === 'sales' && 'date, sale_type, amount, discount, payment_mode, notes'}
                          {type === 'expenses_import' && 'date, category, description, amount, payment_mode'}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="rounded-xl text-xs" onClick={async () => {
                          try {
                            const res = await api.get(`/import/template/${type}`, { responseType: 'blob' });
                            const url = window.URL.createObjectURL(new Blob([res.data]));
                            const link = document.createElement('a'); link.href = url; link.setAttribute('download', `${type}_template.xlsx`);
                            document.body.appendChild(link); link.click(); link.remove();
                          } catch { toast.error('Failed'); }
                        }}><Download size={14} className="mr-1" />Template</Button>
                        <label className="cursor-pointer">
                          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={async (e) => {
                            const file = e.target.files[0]; if (!file) return;
                            try {
                              toast.loading('Importing...');
                              const form = new FormData(); form.append('file', file); form.append('data_type', type);
                              const res = await api.post('/import/data', form, { headers: { 'Content-Type': 'multipart/form-data' } });
                              toast.dismiss();
                              toast.success(res.data.message);
                              if (res.data.errors?.length > 0) toast.warning(`${res.data.errors.length} errors`);
                            } catch (err) { toast.dismiss(); toast.error(err.response?.data?.detail || 'Import failed'); }
                            e.target.value = '';
                          }} />
                          <Button size="sm" className="rounded-xl text-xs" asChild><span><Upload size={14} className="mr-1" />Upload</span></Button>
                        </label>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="backup">
            <SettingsBackupTab />
          </TabsContent>

          <TabsContent value="scheduler">
            <SettingsSchedulerTab
              schedulerJobs={schedulerJobs}
              setSchedulerJobs={setSchedulerJobs}
              schedulerLogs={schedulerLogs}
              setSchedulerLogs={setSchedulerLogs}
            />
          </TabsContent>

          <TabsContent value="cctv">
            <SettingsCCTVTab
              cctvSettings={cctvSettings}
              setCctvSettings={setCctvSettings}
              saveHikConnect={saveHikConnect}
              saveCctvSettings={saveCctvSettings}
              dvrs={dvrs}
              newDVR={newDVR}
              setNewDVR={setNewDVR}
              addDVR={addDVR}
              deleteDVR={deleteDVR}
              branches={branches}
              monitoringConfig={monitoringConfig}
              setMonitoringConfig={setMonitoringConfig}
              saveMonitoringConfig={saveMonitoringConfig}
              runMonitoringNow={runMonitoringNow}
            />
          </TabsContent>


          {/* Access Control Tab */}
          <TabsContent value="access">
            <AccessPoliciesPanel />
          </TabsContent>

          <TabsContent value="deploy">
            <div className="space-y-6">
              <Card className="border-border">
                <CardHeader><CardTitle className="font-outfit">Deploy to Your Website</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">Follow these steps to deploy Smarto on your own website/server:</p>
                  
                  <div className="space-y-4">
                    <div className="p-4 border rounded-xl bg-muted/50">
                      <h3 className="font-semibold text-sm mb-2">Step 1: Save Code to GitHub</h3>
                      <p className="text-xs text-muted-foreground">In the Emergent chat, click <strong>"Save to Github"</strong> button. This pushes all your code to a GitHub repository.</p>
                    </div>
                    
                    <div className="p-4 border rounded-xl bg-muted/50">
                      <h3 className="font-semibold text-sm mb-2">Step 2: Setup MongoDB Atlas (Free)</h3>
                      <ol className="text-xs text-muted-foreground space-y-1 ml-4 list-decimal">
                        <li>Go to <a href="https://www.mongodb.com/atlas" target="_blank" rel="noreferrer" className="text-primary underline">mongodb.com/atlas</a></li>
                        <li>Create free account → Create free cluster (M0)</li>
                        <li>Click Connect → Get connection string</li>
                        <li>Whitelist your server IP (or 0.0.0.0/0 for any)</li>
                      </ol>
                    </div>
                    
                    <div className="p-4 border rounded-xl bg-muted/50">
                      <h3 className="font-semibold text-sm mb-2">Step 3: Deploy (Choose One)</h3>
                      <div className="space-y-3 mt-2">
                        <div className="p-3 bg-white rounded-lg border border-primary/30">
                          <div className="font-medium text-xs flex items-center gap-2"><Badge className="bg-primary text-white">Recommended</Badge> Railway.app (Fastest & Easiest)</div>
                          <ol className="text-xs text-muted-foreground mt-2 ml-4 list-decimal space-y-1">
                            <li>Go to <a href="https://railway.app" target="_blank" rel="noreferrer" className="text-primary underline">railway.app</a> → Sign in with GitHub</li>
                            <li>Click "New Project" → "Deploy from GitHub Repo"</li>
                            <li>Select your repo → It auto-detects the Dockerfile</li>
                            <li>Add environment variables (see Step 4 below)</li>
                            <li>Railway gives you a URL like <code className="bg-muted px-1 rounded">ssctrack.up.railway.app</code></li>
                            <li>In <strong>GoDaddy DNS</strong>: Add a CNAME record pointing your subdomain to Railway URL</li>
                          </ol>
                          <p className="text-xs mt-2 text-primary font-medium">Cost: ~$5/month. Your data is safe on MongoDB Atlas.</p>
                        </div>
                        <div className="p-3 bg-white rounded-lg border">
                          <div className="font-medium text-xs">GoDaddy VPS / Any VPS (Full Control)</div>
                          <ol className="text-xs text-muted-foreground mt-1 ml-4 list-decimal space-y-1">
                            <li>Get a Linux VPS (GoDaddy, DigitalOcean, or Hetzner — $5-10/mo)</li>
                            <li>SSH into server: <code className="bg-muted px-1 rounded">ssh root@your-server-ip</code></li>
                            <li>Install Docker: <code className="bg-muted px-1 rounded">curl -fsSL https://get.docker.com | sh</code></li>
                            <li>Clone repo: <code className="bg-muted px-1 rounded">git clone your-repo-url && cd your-repo</code></li>
                            <li>Create <code className="bg-muted px-1 rounded">.env</code> file with your settings</li>
                            <li>Build & run: <code className="bg-muted px-1 rounded">docker build -t ssctrack . && docker run -d -p 80:80 --env-file .env ssctrack</code></li>
                            <li>In <strong>GoDaddy DNS</strong>: Add an A record pointing your domain to server IP</li>
                          </ol>
                        </div>
                        <div className="p-3 bg-white rounded-lg border">
                          <div className="font-medium text-xs">Render.com (Free Tier Available)</div>
                          <ol className="text-xs text-muted-foreground mt-1 ml-4 list-decimal space-y-1">
                            <li>Go to <a href="https://render.com" target="_blank" rel="noreferrer" className="text-primary underline">render.com</a> → Connect GitHub</li>
                            <li>Create "Web Service" from your repo</li>
                            <li>Set environment variables → Deploy</li>
                            <li>Add custom domain in Render dashboard</li>
                          </ol>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4 border rounded-xl bg-muted/50">
                      <h3 className="font-semibold text-sm mb-2">Step 4: Set Environment Variables</h3>
                      <div className="bg-muted text-stone-100 p-3 rounded-lg text-xs font-mono space-y-1 mt-2">
                        <p>MONGO_URL=mongodb+srv://user:pass@cluster.mongodb.net/ssctrack</p>
                        <p>DB_NAME=ssctrack</p>
                        <p>SECRET_KEY=your-secret-key-here</p>
                        <p>REACT_APP_BACKEND_URL=https://yourdomain.com</p>
                        <p># Optional - for WhatsApp:</p>
                        <p>TWILIO_ACCOUNT_SID=your-sid</p>
                        <p>TWILIO_AUTH_TOKEN=your-token</p>
                        <p>TWILIO_PHONE_NUMBER=+1234567890</p>
                        <p># Optional - for Invoice OCR:</p>
                        <p>EMERGENT_LLM_KEY=your-key</p>
                      </div>
                    </div>

                    <div className="p-4 border rounded-xl bg-muted/50">
                      <h3 className="font-semibold text-sm mb-2">GoDaddy Domain Setup</h3>
                      <ol className="text-xs text-muted-foreground space-y-1 ml-4 list-decimal">
                        <li>Login to <a href="https://dcc.godaddy.com" target="_blank" rel="noreferrer" className="text-primary underline">GoDaddy Domain Manager</a></li>
                        <li>Select your domain → DNS → Manage Records</li>
                        <li><strong>For Railway/Render:</strong> Add CNAME record → Name: <code className="bg-muted px-1 rounded">app</code> → Value: <code className="bg-muted px-1 rounded">your-app.up.railway.app</code></li>
                        <li><strong>For VPS:</strong> Add A record → Name: <code className="bg-muted px-1 rounded">app</code> → Value: <code className="bg-muted px-1 rounded">your-server-ip</code></li>
                        <li>Wait 10-30 min for DNS to propagate</li>
                        <li>Your app will be live at <code className="bg-muted px-1 rounded">app.yourdomain.com</code></li>
                      </ol>
                    </div>
                    
                    <div className="p-4 border rounded-xl bg-orange-50 border-orange-200">
                      <h3 className="font-semibold text-sm mb-2 text-orange-800">Step 5: Import Your Data</h3>
                      <p className="text-xs text-orange-700">After deploying, go to Settings → Import Data tab to upload your existing data from Excel files.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border">
                <CardHeader><CardTitle className="font-outfit">Install App on Phones (PWA)</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-muted-foreground">Smarto is a Progressive Web App — employees can install it like a native app on their phones. No app store needed!</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 border rounded-xl bg-muted/50">
                      <h3 className="font-semibold text-sm mb-2">Android (Chrome)</h3>
                      <ol className="text-xs text-muted-foreground space-y-1 ml-4 list-decimal">
                        <li>Open your app URL in Chrome browser</li>
                        <li>Login with their account</li>
                        <li>Tap the <strong>3 dots menu</strong> (top right)</li>
                        <li>Tap <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong></li>
                        <li>Confirm → App icon appears on home screen</li>
                      </ol>
                    </div>
                    <div className="p-4 border rounded-xl bg-muted/50">
                      <h3 className="font-semibold text-sm mb-2">iPhone (Safari)</h3>
                      <ol className="text-xs text-muted-foreground space-y-1 ml-4 list-decimal">
                        <li>Open your app URL in <strong>Safari</strong> (must be Safari)</li>
                        <li>Login with their account</li>
                        <li>Tap the <strong>Share button</strong> (square with arrow)</li>
                        <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                        <li>Confirm → App icon appears on home screen</li>
                      </ol>
                    </div>
                  </div>
                  <div className="p-3 bg-primary/5 rounded-xl border border-primary/20">
                    <p className="text-xs"><strong>Tip:</strong> Create employee accounts in Users page with appropriate permissions. Each employee logs in with their own account and only sees the modules they have access to (Sales, Kitchen, etc.)</p>
                  </div>
                </CardContent>
              </Card>

              {/* Guided Tours Reset */}
              <Card className="border-border">
                <CardHeader><CardTitle className="font-outfit text-base">Guided Tours</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">Reset all in-app guided tours to replay them on each module page.</p>
                  <ResetModuleTourButton />
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}


function AccessPoliciesPanel() {
  const [policies, setPolicies] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchPolicies(); }, []);

  const fetchPolicies = async () => {
    try {
      const res = await api.get('/access-policies');
      setPolicies(res.data);
    } catch { }
    finally { setLoading(false); }
  };

  const savePolicies = async () => {
    setSaving(true);
    try {
      const res = await api.put('/access-policies', policies);
      setPolicies(res.data);
      toast.success('Access policies saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const updateDeletePolicy = (module, value) => {
    setPolicies(prev => ({ ...prev, delete_policy: { ...prev.delete_policy, [module]: value } }));
  };

  const updateVisibility = (key, value) => {
    setPolicies(prev => ({ ...prev, visibility: { ...prev.visibility, [key]: value } }));
  };

  if (loading || !policies) return <div className="text-center py-8 text-muted-foreground">Loading...</div>;

  const DELETE_OPTIONS = [
    { value: 'anyone', label: 'Anyone with write access' },
    { value: 'admin_manager', label: 'Admin & Manager only' },
    { value: 'admin_only', label: 'Admin only' },
    { value: 'no_delete', label: 'Disabled (no one)' },
  ];

  const MODULES = [
    { key: 'sales', label: 'Sales' },
    { key: 'expenses', label: 'Expenses' },
    { key: 'supplier_payments', label: 'Supplier Payments' },
    { key: 'stock', label: 'Stock Items' },
    { key: 'customers', label: 'Customers' },
    { key: 'invoices', label: 'Invoices' },
    { key: 'employees', label: 'Employees' },
  ];

  return (
    <div className="space-y-6" data-testid="access-policies-panel">
      {/* Delete Restrictions */}
      <Card>
        <CardHeader>
          <CardTitle className="font-outfit text-base flex items-center gap-2"><Trash2 size={16} /> Delete Restrictions</CardTitle>
          <CardDescription className="text-xs">Control who can delete records in each module</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            {MODULES.map(m => (
              <div key={m.key} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg border bg-muted/50 dark:bg-foreground/30" data-testid={`delete-policy-${m.key}`}>
                <span className="text-sm font-medium">{m.label}</span>
                <Select value={policies.delete_policy?.[m.key] || 'admin_manager'} onValueChange={(v) => updateDeletePolicy(m.key, v)}>
                  <SelectTrigger className="w-[200px] h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DELETE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          {/* Time-based restriction */}
          <div className="p-4 rounded-lg border bg-warning/10 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Time-Based Delete Limit</p>
                <p className="text-xs text-muted-foreground">Prevent deletion of records older than a set time (except admin)</p>
              </div>
              <Switch
                checked={policies.delete_time_limit_enabled || false}
                onCheckedChange={(v) => setPolicies(prev => ({ ...prev, delete_time_limit_enabled: v }))}
                data-testid="time-limit-toggle"
              />
            </div>
            {policies.delete_time_limit_enabled && (
              <div className="flex items-center gap-2">
                <Label className="text-xs whitespace-nowrap">Max age (hours):</Label>
                <Input
                  type="number"
                  min={1}
                  max={720}
                  value={policies.delete_time_limit_hours || 24}
                  onChange={(e) => setPolicies(prev => ({ ...prev, delete_time_limit_hours: parseInt(e.target.value) || 24 }))}
                  className="w-24 h-8"
                  data-testid="time-limit-hours"
                />
                <span className="text-xs text-muted-foreground">({((policies.delete_time_limit_hours || 24) / 24).toFixed(1)} days)</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Visibility Restrictions */}
      <Card>
        <CardHeader>
          <CardTitle className="font-outfit text-base flex items-center gap-2"><EyeOff size={16} /> Operator Visibility</CardTitle>
          <CardDescription className="text-xs">Control what operators can see (admins & managers always see everything)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[
              { key: 'operator_hide_financials', label: 'Hide financial summaries', desc: 'Hides total sales, profit, margins on Dashboard' },
              { key: 'operator_hide_profit', label: 'Hide profit & net figures', desc: 'Hides net profit, margins on POS and reports' },
              { key: 'operator_hide_analytics', label: 'Hide analytics pages', desc: 'Blocks access to Analytics & Advanced Analytics' },
              { key: 'operator_hide_reports', label: 'Hide report pages', desc: 'Blocks access to all report pages' },
              { key: 'operator_hide_supplier_credit', label: 'Hide supplier credit info', desc: 'Hides credit limits and outstanding balances' },
              { key: 'operator_hide_employee_salary', label: 'Hide employee salaries', desc: 'Hides salary information in employee pages' },
            ].map(item => (
              <div key={item.key} className="flex items-center justify-between p-3 rounded-lg border bg-muted/50 dark:bg-foreground/30" data-testid={`visibility-${item.key}`}>
                <div>
                  <p className="text-sm font-medium">{item.label}</p>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
                <Switch
                  checked={policies.visibility?.[item.key] || false}
                  onCheckedChange={(v) => updateVisibility(item.key, v)}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Button onClick={savePolicies} disabled={saving} className="rounded-xl" data-testid="save-policies-btn">
        {saving ? 'Saving...' : 'Save Access Policies'}
      </Button>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * Industry & Chart-of-Accounts seeder (used in Company tab)
 * ════════════════════════════════════════════════════════════════════ */
function IndustryCoASeeder() {
  const [industries, setIndustries] = useState([]);
  const [current, setCurrent] = useState('');
  const [picked, setPicked] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [cat, tenant] = await Promise.all([
          api.get('/accounting/industry-coa'),
          api.get('/tenants/current').catch(() => ({ data: {} })),
        ]);
        setIndustries(cat.data || []);
        const ind = tenant.data?.industry || 'generic';
        setCurrent(ind);
        setPicked(ind);
      } catch { /* ignore */ }
    })();
  }, []);

  const seed = async () => {
    if (!picked) return;
    setBusy(true);
    try {
      const r = await api.post(`/accounting/accounts/seed-by-industry?industry=${picked}`);
      setLastResult(r.data);
      setCurrent(picked);
      toast.success(r.data?.message || 'Chart of accounts seeded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to seed');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Pick your industry to seed the base Chart of Accounts (Assets · Liabilities · Equity · Revenue · Expenses) plus
        industry-specific lines (e.g., Shrinkage for Retail, Project Costs for Construction, Pharmacy Sales for Healthcare).
        <br />Existing accounts are never overwritten — only missing ones are added.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {industries.map(ind => (
          <label
            key={ind.id}
            className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-all ${picked === ind.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
            data-testid={`coa-industry-${ind.id}`}
          >
            <input
              type="radio"
              name="industry"
              checked={picked === ind.id}
              onChange={() => setPicked(ind.id)}
              className="mt-1 accent-primary"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm">{ind.label}</span>
                {current === ind.id && <Badge className="text-[10px] bg-success/20 text-success border-0">current</Badge>}
                {ind.extra_accounts > 0 && <Badge variant="outline" className="text-[10px]">+{ind.extra_accounts} extra</Badge>}
              </div>
            </div>
          </label>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Button onClick={seed} disabled={!picked || busy} data-testid="coa-seed-btn" className="rounded-xl">
          {busy ? 'Seeding…' : `Seed ${industries.find(i => i.id === picked)?.label || 'industry'} Chart of Accounts`}
        </Button>
        <Button asChild variant="outline" className="rounded-xl" data-testid="coa-open-btn">
          <a href="/chart-of-accounts">Open Chart of Accounts</a>
        </Button>
      </div>

      {lastResult && (
        <div className="p-3 rounded-lg border border-success/30 bg-success/5 text-xs">
          <strong className="text-success">✓ Seeded:</strong> {lastResult.base_seeded} base + {lastResult.industry_added} {lastResult.industry} accounts · total {lastResult.total_accounts} accounts now available.
        </div>
      )}
    </div>
  );
}



function FixHistoricalCreditSales() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (!window.confirm('This will recompute credit_amount on every sale using payment_details. Safe — no data is deleted. Continue?')) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post('/sales/recalc-credit');
      setResult(r.data);
      toast.success(`Recalculated ${r.data.total_sales} sales — ${r.data.updated} rows fixed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recalc failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="fix-credit-sales-block">
      <p className="text-xs text-muted-foreground">
        Legacy versions stored <strong>credit_amount = final_amount</strong> on online-platform sales (bug, now fixed).
        Click below to rebuild the <code>credit_amount</code> field on every sale using the current <code>payment_details</code>, so the Credit Sales Report / Dashboard widgets match reality.
      </p>
      <div className="flex items-center gap-2">
        <Button onClick={run} disabled={busy} data-testid="fix-credit-sales-btn" className="rounded-xl">
          {busy ? 'Recalculating…' : 'Fix Historical Credit Sales'}
        </Button>
      </div>
      {result && (
        <div className="p-3 rounded-lg border border-success/30 bg-success/5 text-xs">
          <strong className="text-success">✓ Done:</strong> {result.updated} of {result.total_sales} sales updated. {result.updated === 0 ? 'Your data was already clean.' : 'Credit Sales Report should now show correct totals.'}
        </div>
      )}
    </div>
  );
}


function PurgeTestData() {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [executed, setExecuted] = useState(null);
  const [opts, setOpts] = useState({ employees: true, users: true, partners: true, items: true });

  const buildParams = (extra = {}) => ({
    include_employees: opts.employees,
    include_users: opts.users,
    include_partners: opts.partners,
    include_items: opts.items,
    ...extra,
  });

  const runPreview = async () => {
    setBusy(true); setExecuted(null);
    try {
      const r = await api.post('/admin/purge-test-data', null, { params: buildParams() });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally { setBusy(false); }
  };

  const runExecute = async () => {
    const s = preview?.summary || {};
    const totalEntities = (s.branches || 0) + (s.customers || 0) + (s.suppliers || 0)
      + (s.employees || 0) + (s.users || 0) + (s.partners || 0) + (s.items || 0);
    const confirmMsg = `Permanently DELETE:\n\n` +
      `• ${s.branches || 0} branches\n` +
      `• ${s.customers || 0} customers\n` +
      `• ${s.suppliers || 0} suppliers\n` +
      `• ${s.employees || 0} employees\n` +
      `• ${s.users || 0} users\n` +
      `• ${s.partners || 0} partners\n` +
      `• ${s.items || 0} inventory items\n` +
      `• ${s.total_linked || 0} linked records (sales/expenses/payments/stock/loans/attendance)\n\n` +
      `Protected accounts (${(preview?.protected_emails || []).join(', ') || 'ss@ssc.com'}) and your own account are NEVER deleted.\n\n` +
      `This CANNOT be undone. Continue?`;
    if (!window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      const r = await api.post('/admin/purge-test-data', null, { params: buildParams({ confirm: true }) });
      setExecuted(r.data);
      setPreview(null);
      toast.success(`Test data purged — ${totalEntities} entities + linked records deleted`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purge failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="purge-test-data-block">
      <div>
        <p className="text-sm font-medium text-foreground">Delete Test / Demo Data</p>
        <p className="text-xs text-muted-foreground mt-1">
          Scans branches, customers, suppliers, employees, users, partners and inventory items for common test/demo patterns
          (<code>Test</code>, <code>TEST_</code>, <code>Demo</code>, <code>Sample</code>, <code>iter###</code>, <code>@example.com</code>, <code>@test.com</code>, etc.)
          along with all their linked records (sales / expenses / payments / stock / loans / attendance).
          <strong> ss@ssc.com and your own account are always protected.</strong>
          Always runs a <strong>preview first</strong> so you can see exactly what will be removed.
        </p>
      </div>

      {/* Per-entity opt-out checkboxes — branches/customers/suppliers are always on */}
      <div className="flex flex-wrap gap-3 p-2.5 rounded-lg border border-border bg-muted/20" data-testid="purge-opts">
        <span className="text-[11px] text-muted-foreground self-center mr-1">Also scan:</span>
        {[
          { k: 'employees', l: 'Employees' },
          { k: 'users', l: 'Users' },
          { k: 'partners', l: 'Partners' },
          { k: 'items', l: 'Inventory items' },
        ].map(o => (
          <label key={o.k} className="flex items-center gap-1.5 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={opts[o.k]}
              onChange={(e) => setOpts(prev => ({ ...prev, [o.k]: e.target.checked }))}
              className="h-3.5 w-3.5"
              data-testid={`purge-opt-${o.k}`}
            />
            <span>{o.l}</span>
          </label>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Button onClick={runPreview} disabled={busy} variant="outline" data-testid="purge-preview-btn" className="rounded-xl">
          {busy ? 'Scanning…' : 'Preview Test Data'}
        </Button>
        {preview && (
          <Button onClick={runExecute} disabled={busy} variant="destructive" data-testid="purge-execute-btn" className="rounded-xl">
            {busy ? 'Deleting…' : `Delete ${
              (preview.summary.branches || 0) + (preview.summary.customers || 0) + (preview.summary.suppliers || 0) +
              (preview.summary.employees || 0) + (preview.summary.users || 0) + (preview.summary.partners || 0) +
              (preview.summary.items || 0)
            } entities + ${preview.summary.total_linked} linked`}
          </Button>
        )}
      </div>
      {preview && (
        <div className="p-3 rounded-lg border border-warning/30 bg-warning/5 text-xs space-y-2" data-testid="purge-preview-card">
          <p className="font-semibold text-warning">Preview — nothing deleted yet</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              ['branches', 'Branches'],
              ['customers', 'Customers'],
              ['suppliers', 'Suppliers'],
              ['employees', 'Employees'],
              ['users', 'Users'],
              ['partners', 'Partners'],
              ['items', 'Inventory Items'],
            ].map(([key, label]) => {
              const list = preview.preview[key] || [];
              if (list.length === 0) return null;
              return (
                <div key={key} data-testid={`purge-preview-${key}`}>
                  <p className="font-semibold text-foreground mb-1">{label} ({list.length})</p>
                  <ul className="text-muted-foreground space-y-0.5 max-h-32 overflow-y-auto">
                    {list.slice(0, 10).map(r => (
                      <li key={r.id || r.email || r.name}>• {r.name || r.email || '(no name)'}{r.email && r.name ? ` — ${r.email}` : ''}</li>
                    ))}
                    {list.length > 10 && <li className="italic">… and {list.length - 10} more</li>}
                  </ul>
                </div>
              );
            })}
            {preview.preview.linked_records && Object.keys(preview.preview.linked_records).length > 0 && (
              <div className="sm:col-span-2">
                <p className="font-semibold text-foreground mb-1">Linked records (will also be deleted)</p>
                <ul className="text-muted-foreground space-y-0.5 grid grid-cols-2 gap-x-3">
                  {Object.entries(preview.preview.linked_records).map(([k, v]) => (
                    <li key={k}>• {k.replace(/_/g, ' ')}: <strong>{v}</strong></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {preview.protected_emails?.length > 0 && (
            <p className="text-[11px] text-success mt-2">
              ✓ Protected accounts skipped: {preview.protected_emails.join(', ')} + your own account
            </p>
          )}
        </div>
      )}
      {executed && (
        <div className="p-3 rounded-lg border border-success/30 bg-success/5 text-xs" data-testid="purge-executed-card">
          <p className="font-semibold text-success">✓ Purged successfully</p>
          <ul className="text-muted-foreground space-y-0.5 mt-1 grid grid-cols-2 gap-x-3">
            {Object.entries(executed.deleted || {}).filter(([_, v]) => v > 0).map(([k, v]) => (
              <li key={k}>• {k.replace(/_/g, ' ')}: <strong className="text-foreground">{v}</strong> deleted</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ResetTransactionalData() {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [executed, setExecuted] = useState(null);
  const [beforeDate, setBeforeDate] = useState('');
  const [confirmText, setConfirmText] = useState('');

  const params = (extra = {}) => ({ ...(beforeDate ? { before_date: beforeDate } : {}), ...extra });

  const runPreview = async () => {
    setBusy(true); setExecuted(null);
    try {
      const r = await api.post('/admin/reset-transactional-data', null, { params: params() });
      setPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Preview failed'); }
    finally { setBusy(false); }
  };

  const runExecute = async () => {
    if (confirmText.trim().toUpperCase() !== 'RESET') { toast.error('Type RESET to confirm'); return; }
    if (!window.confirm(`This permanently deletes ${preview?.total_records || 0} transactional records${beforeDate ? ` dated before ${beforeDate}` : ''} (sales, expenses, journal entries, payments, stock movements, payroll, banking…). Master data (accounts, branches, customers, suppliers, items, employees, users, settings) is KEPT. This CANNOT be undone. Continue?`)) return;
    setBusy(true);
    try {
      const r = await api.post('/admin/reset-transactional-data', null, { params: params({ confirm: true, confirmation_text: 'RESET' }) });
      setExecuted(r.data); setPreview(null); setConfirmText('');
      toast.success(`Reset complete — ${r.data.total_deleted} records deleted`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Reset failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 pt-4 mt-4 border-t border-border" data-testid="reset-transactional-block">
      <div>
        <p className="text-sm font-medium text-foreground">Onboarding Reset — Clear Transactional Data</p>
        <p className="text-xs text-muted-foreground mt-1">
          Wipes accumulating <strong>transactional &amp; ledger</strong> data (sales, expenses, journal entries, payments,
          POS orders, stock movements, payroll, banking &amp; reconciliation) so your reports start from a clean slate —
          <strong> orphan-free</strong> (journal entries are cleared too, so the ledger reconciles to zero).
          <strong className="text-success"> Master &amp; config data is always KEPT</strong> (chart of accounts, branches,
          customers, suppliers, items, employees, users, settings). Optionally clear only records before a cut-off date
          (your go-live date). Always preview first.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 p-2.5 rounded-lg border border-border bg-muted/20">
        <div>
          <label className="text-[11px] text-muted-foreground block mb-1">Only delete before (optional)</label>
          <input type="date" value={beforeDate} onChange={(e) => setBeforeDate(e.target.value)}
            className="h-8 rounded-lg border border-border bg-background px-2 text-xs" data-testid="reset-before-date" />
        </div>
        <Button onClick={runPreview} disabled={busy} variant="outline" className="rounded-xl" data-testid="reset-preview-btn">
          {busy ? 'Scanning…' : 'Preview'}
        </Button>
      </div>

      {preview && (
        <div className="p-3 rounded-lg border border-warning/30 bg-warning/5 text-xs space-y-2" data-testid="reset-preview-card">
          <p className="font-semibold text-warning">Preview — nothing deleted yet · {preview.total_records} records ({preview.scope})</p>
          <ul className="text-muted-foreground grid grid-cols-2 gap-x-3 gap-y-0.5 max-h-48 overflow-y-auto">
            {Object.entries(preview.counts || {}).map(([k, v]) => (
              <li key={k}>• {k.replace(/_/g, ' ')}: <strong className="text-foreground">{v}</strong></li>
            ))}
          </ul>
          {preview.total_records > 0 && (
            <div className="flex items-center gap-2 pt-1">
              <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Type RESET to confirm"
                className="h-8 rounded-lg border border-error/40 bg-background px-2 text-xs w-44" data-testid="reset-confirm-input" />
              <Button onClick={runExecute} disabled={busy || confirmText.trim().toUpperCase() !== 'RESET'}
                variant="destructive" className="rounded-xl" data-testid="reset-execute-btn">
                {busy ? 'Deleting…' : `Delete ${preview.total_records} records`}
              </Button>
            </div>
          )}
        </div>
      )}
      {executed && (
        <div className="p-3 rounded-lg border border-success/30 bg-success/5 text-xs" data-testid="reset-executed-card">
          <p className="font-semibold text-success">✓ Reset complete — {executed.total_deleted} records deleted</p>
          <ul className="text-muted-foreground grid grid-cols-2 gap-x-3 mt-1">
            {Object.entries(executed.deleted || {}).filter(([_, v]) => typeof v === 'number' && v > 0).map(([k, v]) => (
              <li key={k}>• {k.replace(/_/g, ' ')}: <strong className="text-foreground">{v}</strong></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


